'use client';

import type { listLocationsForOrganizationAction } from '@/actions/locations';
import { ACTIVE_LOCATION_COOKIE_MAX_AGE, ACTIVE_LOCATION_COOKIE_NAME, ACTIVE_LOCATION_STORAGE_KEY } from '@/lib/active-location';
import { createContext, useContext, useEffect, useState } from 'react';
type Location = Awaited<ReturnType<typeof listLocationsForOrganizationAction>>[number];
type ActiveLocationContextType = {
  activeLocation: Location | null;
  setActiveLocation: (location: Location | null) => void;
};
const ActiveLocationContext = createContext<ActiveLocationContextType | undefined>(undefined);
export function ActiveLocationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [activeLocation, setActiveLocationState] = useState<Location | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(ACTIVE_LOCATION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Location;
        setActiveLocationState(parsed);
      }
    } catch (error) {
      console.error('Failed to load active location from localStorage:', error);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  // Save to localStorage whenever activeLocation changes
  useEffect(() => {
    if (!isInitialized || typeof window === 'undefined') return;
    try {
      if (activeLocation) {
        localStorage.setItem(ACTIVE_LOCATION_STORAGE_KEY, JSON.stringify(activeLocation));
        document.cookie = `${ACTIVE_LOCATION_COOKIE_NAME}=${encodeURIComponent(activeLocation.id)}; path=/; max-age=${ACTIVE_LOCATION_COOKIE_MAX_AGE}`;
      } else {
        localStorage.removeItem(ACTIVE_LOCATION_STORAGE_KEY);
        document.cookie = `${ACTIVE_LOCATION_COOKIE_NAME}=; path=/; max-age=0`;
      }
    } catch (error_0) {
      console.error('Failed to save active location to localStorage:', error_0);
    }
  }, [activeLocation, isInitialized]);
  const setActiveLocation = (location: Location | null) => {
    setActiveLocationState(location);
  };
  return <ActiveLocationContext.Provider value={{
    activeLocation,
    setActiveLocation
  }}>
            {children}
        </ActiveLocationContext.Provider>;
}
export function useActiveLocation() {
  const context = useContext(ActiveLocationContext);
  if (context === undefined) {
    throw new Error("useActiveLocation must be used within an ActiveLocationProvider");
  }
  return context;
}